import { BEHAVIOUR_TYPES, behaviourToFieldMeta } from './behaviourCatalog';
import { fieldRef, previousRaRef, constant, op } from './astHelpers';
import { buildPercentageFormula } from './buildPercentageFormula';
import { buildSumFormula } from './buildSumFormula';
import { buildSubtractFormula } from './buildSubtractFormula';
import { buildPreviousRaFormula } from './buildPreviousRaFormula';
import { buildCappedFormula } from './buildCappedFormula';
import { buildBalanceRecoveryFormula } from './buildBalanceRecoveryFormula';
import { buildConditionalFormula } from './buildConditionalFormula';
import { buildQuantityRateFormula } from './buildQuantityRateFormula';

/**
 * Compile visual formula blocks into AST.
 * blocks: [{ kind: 'FIELD'|'CONSTANT'|'PERCENT'|'PREVIOUS'|'OP', ... }]
 */
export const buildAstFromFormulaBlocks = (blocks) => {
  if (!Array.isArray(blocks) || !blocks.length) return null;
  const values = [];
  let pendingOp = null;

  const pushValue = (node) => {
    if (!pendingOp || !values.length) {
      values.push(node);
      return;
    }
    const left = values.pop();
    const opName = pendingOp;
    pendingOp = null;
    if (opName === 'PERCENT_OF') {
      values.push(op('PERCENTAGE', [left, node]));
    } else {
      values.push(op(opName, [left, node]));
    }
  };

  blocks.forEach((b) => {
    if (!b) return;
    const kind = String(b.kind || '').toUpperCase();
    if (kind === 'OP') {
      pendingOp = String(b.operator || 'ADD').toUpperCase();
      return;
    }
    if (kind === 'FIELD') {
      pushValue(fieldRef(b.fieldCode));
      return;
    }
    if (kind === 'PREVIOUS') {
      pushValue(previousRaRef(b.fieldCode, b.fallback ?? 0));
      return;
    }
    if (kind === 'CONSTANT' || kind === 'PERCENT') {
      pushValue(constant(b.value));
    }
  });

  if (values.length === 1) return values[0];
  if (values.length > 1) return op('ADD', values);
  return null;
};

export const buildFormulaFromBehaviour = (cfg = {}, context = {}) => {
  const behaviour = String(cfg.behaviourType || BEHAVIOUR_TYPES.MANUAL_ENTRY);
  const selfCode = context.selfFieldCode || '';

  switch (behaviour) {
    case BEHAVIOUR_TYPES.MANUAL_ENTRY:
    case BEHAVIOUR_TYPES.AUTO_LOOKUP:
    case BEHAVIOUR_TYPES.REFERENCE_ONLY:
    case BEHAVIOUR_TYPES.WITHHELD:
    case BEHAVIOUR_TYPES.RELEASE:
      return null;

    case BEHAVIOUR_TYPES.PREVIOUS_RA_VALUE:
      return buildPreviousRaFormula(cfg, selfCode);

    case BEHAVIOUR_TYPES.SUM_OF_FIELDS:
    case BEHAVIOUR_TYPES.AUTO_TOTAL:
      return buildSumFormula(cfg);

    case BEHAVIOUR_TYPES.SUBTRACT_FIELDS:
      return buildSubtractFormula(cfg);

    case BEHAVIOUR_TYPES.PERCENTAGE:
      return buildPercentageFormula(cfg);

    case BEHAVIOUR_TYPES.CAPPED_PERCENTAGE:
      return buildCappedFormula(cfg);

    case BEHAVIOUR_TYPES.BALANCE_RECOVERY:
      return buildBalanceRecoveryFormula(cfg);

    case BEHAVIOUR_TYPES.QUANTITY_RATE:
      return buildQuantityRateFormula(cfg);

    case BEHAVIOUR_TYPES.CONDITIONAL:
      return buildConditionalFormula(cfg);

    case BEHAVIOUR_TYPES.CURRENT_MINUS_PREVIOUS:
    case BEHAVIOUR_TYPES.INCREMENTAL_VALUE: {
      const code = cfg.baseFieldCode || selfCode;
      if (!code) return null;
      return op('SUBTRACT', [fieldRef(code), previousRaRef(cfg.previousFieldCode || code, 0)]);
    }

    case BEHAVIOUR_TYPES.CUMULATIVE_VALUE: {
      const code = cfg.baseFieldCode || selfCode;
      if (!code) return null;
      return op('ADD', [previousRaRef(cfg.previousFieldCode || code, 0), fieldRef(code)]);
    }

    case BEHAVIOUR_TYPES.CUSTOM_FORMULA:
      if (Array.isArray(cfg.formulaBlocks) && cfg.formulaBlocks.length) {
        return buildAstFromFormulaBlocks(cfg.formulaBlocks);
      }
      return context.existingFormulaJson || null;

    default:
      return context.existingFormulaJson || null;
  }
};

/**
 * Collect field codes that contribute to an include-in group.
 */
export const collectIncludeInMembers = (fields, group) => {
  if (!group || group === 'NONE') return [];
  return (fields || [])
    .filter((f) => {
      const st = String(f.status || 'ACTIVE').toUpperCase();
      if (st === 'INACTIVE' || st === 'DEPRECATED') return false;
      const bc = f.metadata?.businessConfig;
      return bc && String(bc.includeInGroup || '') === group;
    })
    .map((f) => f.fieldCode)
    .filter(Boolean);
};

/**
 * Apply auto-total SUM compilation across all fields before save.
 * AUTO_TOTAL fields get sumFieldCodes from includeInGroup members.
 * Explicit CUSTOM_FORMULA totals are left alone.
 */
export const applyAutoTotals = (fields) => {
  const list = Array.isArray(fields) ? fields : [];
  return list.map((field) => {
    const cfg = field.metadata?.businessConfig;
    if (!cfg || cfg.behaviourType !== BEHAVIOUR_TYPES.AUTO_TOTAL) return field;
    const group = cfg.includeInGroup && cfg.includeInGroup !== 'NONE'
      ? cfg.includeInGroup
      : (field.semanticRole || null);
    if (!group) return field;
    const members = collectIncludeInMembers(list, group).filter((c) => c !== field.fieldCode);
    const nextCfg = { ...cfg, sumFieldCodes: members, behaviourType: BEHAVIOUR_TYPES.AUTO_TOTAL };
    const formulaJson = buildSumFormula(nextCfg);
    return {
      ...field,
      fieldType: 'FORMULA',
      sourceType: 'FORMULA',
      formulaJson,
      metadata: {
        ...(field.metadata || {}),
        businessConfig: nextCfg,
      },
    };
  });
};

/**
 * Compile one field's businessConfig into formulaJson + field meta.
 */
export const compileField = (field, allFields = []) => {
  const cfg = field.metadata?.businessConfig;
  if (!cfg) return field;

  let working = { ...cfg };
  if (cfg.behaviourType === BEHAVIOUR_TYPES.AUTO_TOTAL) {
    const group = cfg.includeInGroup && cfg.includeInGroup !== 'NONE'
      ? cfg.includeInGroup
      : field.semanticRole;
    if (group) {
      working = {
        ...working,
        sumFieldCodes: collectIncludeInMembers(allFields, group).filter((c) => c !== field.fieldCode),
      };
    }
  }

  const meta = behaviourToFieldMeta(working.behaviourType);
  let formulaJson = buildFormulaFromBehaviour(working, {
    selfFieldCode: field.fieldCode,
    existingFormulaJson: field.formulaJson,
  });

  // Preserve unmatched custom AST
  if (working.behaviourType === BEHAVIOUR_TYPES.CUSTOM_FORMULA && !formulaJson && field.formulaJson) {
    formulaJson = field.formulaJson;
  }

  return {
    ...field,
    fieldType: meta.fieldType,
    sourceType: meta.sourceType,
    formulaJson: formulaJson ?? (working.behaviourType === BEHAVIOUR_TYPES.CUSTOM_FORMULA ? field.formulaJson : null),
    metadata: {
      ...(field.metadata || {}),
      businessConfig: working,
    },
  };
};

/** Compile entire template field list for save. */
export const rewriteFieldRefsToCodes = (formulaJson, fields = []) => {
  if (!formulaJson || typeof formulaJson !== 'object') return formulaJson;
  const byCode = new Set((fields || []).map((f) => f.fieldCode));
  const byLabel = new Map(
    (fields || []).map((f) => [String(f.fieldLabel || '').trim().toLowerCase(), f.fieldCode]),
  );

  const walk = (node) => {
    if (!node || typeof node !== 'object') return node;
    if (Array.isArray(node)) return node.map(walk);
    if (node.type === 'FIELD' && node.fieldCode && !byCode.has(node.fieldCode)) {
      const resolved = byLabel.get(String(node.fieldCode).trim().toLowerCase());
      if (resolved) return { ...node, fieldCode: resolved };
    }
    const out = { ...node };
    if (Array.isArray(node.arguments)) out.arguments = node.arguments.map(walk);
    ['left', 'right', 'condition', 'then', 'else'].forEach((k) => {
      if (node[k]) out[k] = walk(node[k]);
    });
    return out;
  };

  return walk(formulaJson);
};

/** Compile entire template field list for save. */
export const compileTemplateFields = (fields) => {
  const withTotals = applyAutoTotals(fields);
  return withTotals.map((f) => {
    const compiled = compileField(f, withTotals);
    return {
      ...compiled,
      formulaJson: rewriteFieldRefsToCodes(compiled.formulaJson, withTotals),
    };
  });
};
